import { Text } from "@oysterhr/ui"
import FlagAndCountry from "~/app/components/FlagAndCountry"
import classNames from "classnames"
import { useTranslation } from "react-i18next"

import { SalaryCalculation } from "@oysterhr/schema"
import { getAmountWithCurrencyAndCommas } from "../../utils"
import { CalculationActionsPopover } from "../CalculationActionsPopover"
import { useCalculationsProvider } from "../../contexts"

interface CalculationCardProps {
  calculation: SalaryCalculation
  index: number
  isActive: boolean
  onClick: (calculationIndex: number) => void
}

export const CalculationCard = ({
  calculation,
  index,
  isActive,
  onClick,
}: CalculationCardProps) => {
  const { t } = useTranslation("CostCalculator")
  const { cycle } = useCalculationsProvider()

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      onClick(index)
    }
  }

  return (
    <div
      role="button"
      aria-label="Calculation card"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onClick={() => onClick(index)}
      className={classNames("relative w-full border-l-4 bg-white", {
        "border-primary-base": isActive,
        "border-none": !isActive,
      })}
    >
      <div
        className={classNames(
          "flex flex-row space-x-2 overflow-auto border-t border-gray-200 bg-white px-4 py-3",
          {
            " bg-primary-lighten-100": isActive,
            "pl-5": !isActive,
          },
        )}
      >
        <div className="flex flex-1 flex-col space-y-3">
          <div className="flex flex-row items-center justify-between">
            <FlagAndCountry
              countryCode={calculation.country.code}
              countryName={calculation.country.name}
            />
            <CalculationActionsPopover calculation={calculation} index={index} />
          </div>

          <div className="flex flex-row items-center justify-between">
            <Text size="small" className="text-gray-400">
              {cycle === "annual" ? t("annualCosts") : t("monthlyCosts")}
            </Text>
            <Text size="small" className="text-gray-400">
              {getAmountWithCurrencyAndCommas(
                calculation.totals.employerCosts,
                calculation.currency.code,
                cycle,
              )}
            </Text>
          </div>
        </div>
      </div>
    </div>
  )
}
